import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Squiggle from './Squiggle';

// Public URLs of the published templates (Share -> Publish, with "Allow
// duplicate as template" on). Built by scripts/create-notion-template.js
// (DT_TEMPLATE_VARIANT=adr for the second). Until set, the page leads with
// the Slack install instead.
export const NOTION_TEMPLATE_URL = process.env.REACT_APP_NOTION_TEMPLATE_URL || '';
export const NOTION_ADR_TEMPLATE_URL = process.env.REACT_APP_NOTION_ADR_TEMPLATE_URL || '';

const ROWS = [
  { title: 'Ship the new pricing page before the conference', status: 'Awaiting approval', tone: 'butter', health: '', channel: '#growth', slack: true },
  { title: 'Move billing data from MongoDB to Postgres', status: 'Decided', tone: 'mint', health: 'Current', channel: '#eng-platform', slack: true },
  { title: 'Support tickets get a first response in 4 hours', status: 'Decided', tone: 'mint', health: 'Overdue', channel: '#support', slack: false },
  { title: 'Store billing data in MongoDB', status: 'Superseded', tone: 'lilac', health: 'Replaced', channel: '#eng-platform', slack: false },
];

const INSIDE = [
  ['Health that shows staleness', 'Every decision gets a review date. A Health column reads Review soon, then Overdue, so old calls get rechecked on purpose.', 'blush'],
  ['Supersede, don’t rewrite', 'Changed course? Link the new decision to the one it replaces. Both pages show it, and the history stays intact.', 'butter'],
  ['A “Start here” view', 'Everything still in force, by area, with the why. The page to send a new joiner on day one.', 'mint'],
  ['Records worth reading', 'A one-sentence summary, options with trade-offs, concerns raised and how you’ll know it worked. Seven worked examples show the way.', 'lilac'],
];

const QUESTIONS = [
  ['Do I need Decision Tracker to use the template?', 'No. Both templates are free and work on their own, on any Notion plan, including Free. Connecting Slack is what stops a log going stale, because nobody has to remember to copy decisions across.'],
  ['What’s the difference between the two templates?', 'The Living Decision Log is for any team: product, growth, people, operations. Architecture Decision Records uses the same structure with records shaped for engineering: context, decision, considered options, consequences and how you’ll confirm the code follows it.'],
  ['What does the Slack sync copy into Notion?', 'Only decisions someone chose to track: the title and reasoning, who owns it and who approved, status, deadline, outcome, the public channel name and a link to the thread. It never copies private channel names or messages nobody tracked.'],
  ['Will it overwrite what we write in Notion?', 'No. When a decision changes in Slack we update its properties, like status and approvals. The page body is written once, and anything your team adds there is left alone.'],
  ['Can we use a decision log we already have?', 'Yes. When Notion asks which pages to share, pick the page with your log instead of duplicating the template. If a column we write is missing, we add it; columns you renamed or retyped keep working.'],
  ['What if we disconnect?', 'Syncing stops, and your Notion log and everything in it stays exactly where it is. Disconnect any time from the Decision Tracker Home tab in Slack.'],
  ['Which Notion plans does it work with?', 'All of them, including Free. Very large logs on a Free workspace can hit Notion’s block limit; if that happens, syncing pauses and we retry.'],
];

const NotionTemplate = ({ installLink, onCTAClick }) => {
  useEffect(() => {
    const previous = document.title;
    document.title = 'Free Notion decision log template (and ADR template) | Decision Tracker';
    return () => { document.title = previous; };
  }, []);

  const templateButton = NOTION_TEMPLATE_URL && (
    <a className="dt-btn dt-btn-primary" href={NOTION_TEMPLATE_URL} target="_blank" rel="noopener noreferrer">
      Get the Living Decision Log
    </a>
  );
  const adrButton = NOTION_ADR_TEMPLATE_URL && (
    <a className="dt-btn dt-btn-quiet" href={NOTION_ADR_TEMPLATE_URL} target="_blank" rel="noopener noreferrer">
      Architecture Decision Records
    </a>
  );
  const installButton = (primary) => (
    <a className={`dt-btn ${primary ? 'dt-btn-primary' : 'dt-btn-quiet'}`} href={installLink} onClick={onCTAClick}>
      {primary ? 'Add to Slack and connect Notion' : 'Fill it from Slack'}
    </a>
  );

  return (
    <div className="dt">
      <section className="dt-hero dt-page-hero">
        <div className="dt-wrap dt-split dt-notion-hero">
          <div>
            <p className="dt-kicker">Free Notion template</p>
            <h1 className="dt-page-title">
              A decision log that <span className="dt-kept">fills itself<Squiggle /></span> from Slack
            </h1>
            <p className="dt-hero-sub">
              Most decision logs go stale within a month, because someone has to remember to update them. The Living
              Decision Log shows you what’s still true, what needs review, and what replaced what. Then Slack keeps it filled.
            </p>
            <div className="dt-cta-row">
              {templateButton}
              {adrButton}
              {!NOTION_TEMPLATE_URL && installButton(true)}
            </div>
            <p className="dt-fineprint">Both templates are free forever. The Slack sync is included in every Decision Tracker plan.</p>
          </div>

          <div className="dt-stage dt-tone--lilac" aria-label="Preview of the Notion decision log">
            <div className="dt-notion-table" role="table">
              <div className="dt-notion-toolbar" aria-hidden="true">
                <span className="dt-notion-title">🧭 Living Decision Log</span>
                <span className="dt-notion-views"><b>All decisions</b> · Start here · Needs review · History</span>
              </div>
              <div className="dt-notion-row dt-notion-row--head" role="row">
                <span role="columnheader">Decision</span>
                <span role="columnheader">Status</span>
                <span role="columnheader" className="dt-notion-hide-sm">Health</span>
                <span role="columnheader" className="dt-notion-hide-sm dt-notion-hide-md">Source</span>
              </div>
              {ROWS.map((row, i) => (
                <div className={`dt-notion-row${i === 0 ? ' dt-notion-row--new' : ''}`} role="row" key={row.title}>
                  <span role="cell" className="dt-notion-cell-title">{row.title}</span>
                  <span role="cell"><span className={`dt-chip dt-tone--${row.tone}`}>{row.status}</span></span>
                  <span role="cell" className="dt-notion-hide-sm dt-notion-health">{row.health}</span>
                  <span role="cell" className="dt-notion-hide-sm dt-notion-hide-md">
                    {row.slack ? <span className="dt-pill" title={row.channel}>Slack</span> : <span className="dt-notion-muted">Notion</span>}
                  </span>
                </div>
              ))}
            </div>
            <p className="dt-note dt-notion-note" aria-hidden="true">just arrived from a Slack thread ↑</p>
          </div>
        </div>
      </section>

      <section className="dt-section dt-section--surface">
        <div className="dt-wrap">
          <div className="dt-section-head">
            <h2 className="dt-h2">What’s in the template</h2>
            <p className="dt-body">
              Everything a team needs to keep one honest record of what it decided and why. Duplicate it into your
              workspace in one click and delete the example rows when you add your first real decision.
            </p>
          </div>
          <div className="dt-features">
            {INSIDE.map(([title, body, tone]) => (
              <div className={`dt-feature dt-tone--${tone}`} key={title}>
                <span className="dt-feature-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l4 4L19 6" /></svg>
                </span>
                <h3>{title}</h3>
                <p>{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="dt-section">
        <div className="dt-wrap">
          <div className="dt-section-head">
            <h2 className="dt-h2">Then stop copying decisions across by hand</h2>
            <p className="dt-body">
              Decisions get made in Slack threads, not in Notion. Decision Tracker captures them where they happen
              and keeps your Notion log current, with no one assigned to maintain it.
            </p>
          </div>
          <ol className="dt-setup">
            <li className="dt-setup-step">
              <h3 className="dt-h3">Track it in Slack</h3>
              <ul className="dt-step-points">
                <li>Right-click the message where the call was made and choose Track decision.</li>
                <li>The title is filled in from the message, so it takes a couple of seconds.</li>
              </ul>
            </li>
            <li className="dt-setup-step">
              <h3 className="dt-h3">Connect Notion once</h3>
              <ul className="dt-step-points">
                <li>Open Decision Tracker’s Home tab in Slack and click Connect Notion.</li>
                <li>Duplicate the template, or pick a log you already have. Past decisions are added straight away.</li>
              </ul>
            </li>
            <li className="dt-setup-step">
              <h3 className="dt-h3">It stays current on its own</h3>
              <ul className="dt-step-points">
                <li>Approvals, status, deadlines and outcomes update in Notion as they change in Slack.</li>
                <li>Every row links back to the thread, so the context is one click away.</li>
              </ul>
            </li>
          </ol>
          <div className="dt-cta-row">
            {installButton(true)}
            <Link className="dt-btn dt-btn-quiet" to="/getting-started">How Decision Tracker works</Link>
          </div>
        </div>
      </section>

      <section className="dt-section dt-section--surface">
        <div className="dt-wrap dt-faq">
          <h2 className="dt-h2">Questions</h2>
          <div className="dt-faq-list">
            {QUESTIONS.map(([q, a]) => (
              <details className="dt-faq-item" key={q}>
                <summary>{q}</summary>
                <p>{a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

// Where Notion's OAuth callback lands. Messages mirror the error codes set in
// /api/notion/callback and /api/notion/connect in index-worker.js.
const ERRORS = {
  link_expired: ['That link has expired', 'Go back to your Living Decision Log in Notion and click Connect Slack again. If Decision Tracker is already in Slack, it’ll take you straight to Notion.'],
  cancelled: ['Notion isn’t connected yet', 'Decision Tracker is in Slack, but Notion wasn’t connected. Try again, and when Notion asks, search for your Living Decision Log page.'],
  no_pages: ['Notion didn’t share a page with us', 'Try again, and when Notion asks, search for your Living Decision Log page and select it.'],
  not_available: ['Notion sync isn’t switched on yet', 'We’re rolling it out. Your template works on its own in the meantime.'],
  failed: ['Something went wrong connecting Notion', 'Nothing was changed in your Notion workspace. Please try again, or tell us what happened.'],
};

// Only link to real Notion hosts: this page's query string is user-controllable.
const safeNotionUrl = (value) => {
  try {
    const url = new URL(value);
    const host = url.hostname;
    const ok = url.protocol === 'https:' && (host === 'notion.so' || host.endsWith('.notion.so') || host.endsWith('.notion.site') || host === 'notion.com' || host.endsWith('.notion.com'));
    return ok ? url.toString() : null;
  } catch (e) {
    return null;
  }
};

// Same reason: the retry link may only ever point at our own signed connect route.
const safeRetryPath = (value) => (value && /^\/api\/notion\/connect\?[\w=&%.-]+$/.test(value) ? value : null);
const safeTeam = (value) => (value && /^[TE][A-Z0-9]{4,}$/.test(value) ? value : null);

export const NotionConnected = ({ location }) => {
  const params = new URLSearchParams((location && location.search) || '');
  const error = params.get('error');
  const connected = params.get('status') === 'connected';
  const count = Number(params.get('count')) || 0;
  const dbUrl = safeNotionUrl(params.get('db'));
  const logName = params.get('log') || 'your decision log';
  const retry = safeRetryPath(params.get('retry'));
  const team = safeTeam(params.get('team'));
  const slackUrl = team ? `https://app.slack.com/client/${team}` : 'slack://open';

  if (connected && !error) {
    return (
      <div className="dt">
        <section className="dt-hero dt-page-hero">
          <div className="dt-wrap dt-split">
            <div>
              <p className="dt-kicker">Connected</p>
              <h1 className="dt-page-title">Your {logName} is connected</h1>
              <p className="dt-hero-sub">
                Decisions you track in Slack will appear in it within seconds.
                {count > 0 ? ` We’re adding your ${count} existing decision${count === 1 ? '' : 's'} now.` : ''}
              </p>
              <div className="dt-cta-row">
                <a className="dt-btn dt-btn-primary" href={slackUrl}>Track your first decision in Slack</a>
                {dbUrl && <a className="dt-btn dt-btn-quiet" href={dbUrl} target="_blank" rel="noopener noreferrer">Open your log</a>}
              </div>
              {retry && (
                <p className="dt-fineprint">
                  Not the page you meant? <a href={retry}>Choose a different page</a>
                </p>
              )}
            </div>
            <div className="dt-sticky-note dt-sticky-note--steps" aria-label="Your first decision">
              <span className="dt-sticky-pin" />
              <p className="dt-sticky-label">Try it now</p>
              <ol>
                <li>In Slack, hover over a message where something was decided.</li>
                <li>Click <b>⋯</b> and choose <b>Track decision</b>. The form is already filled in.</li>
                <li>Save, then look at {logName} in Notion.</li>
              </ol>
            </div>
          </div>
        </section>
      </div>
    );
  }

  const [title, body] = ERRORS[error] || ERRORS.failed;
  return (
    <div className="dt">
      <section className="dt-hero">
        <div className="dt-wrap">
          <h1 className="dt-h2">{title}</h1>
          <p className="dt-body" style={{ marginTop: 16, maxWidth: '60ch' }}>{body}</p>
          <div className="dt-cta-row">
            {retry
              ? <a className="dt-btn dt-btn-primary" href={retry}>Connect Notion</a>
              : <a className="dt-btn dt-btn-primary" href={slackUrl}>Open Slack</a>}
            <Link className="dt-btn dt-btn-quiet" to="/contact">Contact us</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default NotionTemplate;



// WEBPACK FOOTER //
// ./src/components/NotionTemplate.js